import { Icon } from '@iconify/react';
import classNames from 'classnames';
import type { FC, MouseEvent } from 'react';
import { useEffect, useState } from 'react';

import {
  resumeHiddenHeaderReaction,
  suppressHiddenHeaderReaction,
} from '@/hooks/useHiddenHeader';
import type { TocItem } from '@/utils/blogContent';

import Text from '../Text';

type Props = {
  items: TocItem[];
  className?: string;
};

const TableOfContents: FC<Props> = ({ items, className }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    if (!items.length) return;

    const elements = items
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [topmost] = entries
          .filter(({ isIntersecting }) => isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (topmost) setActiveId(topmost.target.id);
      },
      {
        rootMargin: '-24px 0px -70% 0px',
        threshold: 0,
      }
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [items]);

  const handleItemClick = (
    event: MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    suppressHiddenHeaderReaction();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState(null, '', `#${id}`);

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resumeHiddenHeaderReaction();
      window.removeEventListener('scrollend', settle);
    };
    window.addEventListener('scrollend', settle, { once: true });
    setTimeout(settle, 3000);
  };

  if (!items.length) return null;

  return (
    <nav
      aria-label="Table of contents"
      className={classNames(
        'w-full overflow-hidden rounded-2xl bg-jb-neutral-100 shadow-big-moduls',
        className
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        aria-expanded={isOpen}
        aria-controls="toc-panel"
        className="flex w-full items-center justify-between gap-2 px-5 py-4 text-left transition-colors hover:bg-jb-neutral-200"
      >
        <Text variant="jb-body-medium" className="font-semibold">
          On this page
        </Text>
        <Icon
          icon="material-symbols-light:keyboard-arrow-down"
          fontSize={20}
          className={classNames(
            'shrink-0 text-jb-neutral-800 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <div
        id="toc-panel"
        className={classNames(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <ol className="flex flex-col gap-1 border-t border-jb-neutral-300 px-5 pb-5 pt-3">
            {items.map(({ id, text }) => {
              const isActive = id === activeId;
              return (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    onClick={(event) => handleItemClick(event, id)}
                    aria-current={isActive ? 'location' : undefined}
                    className={classNames(
                      'block border-l-2 py-1.5 pl-3 transition-colors',
                      isActive
                        ? 'border-jb-primary-500 text-jb-primary-600'
                        : 'border-transparent text-jb-neutral-800 hover:text-jb-primary-500'
                    )}
                  >
                    <Text
                      variant="jb-body-small"
                      className={classNames(isActive && 'font-semibold')}
                    >
                      {text}
                    </Text>
                  </a>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </nav>
  );
};

export default TableOfContents;
